// src/App.js — Enterprise B2B layout
// eslint-disable-next-line no-unused-vars
import { useState, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Container,
  Box,
  Typography,
  IconButton,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import Grid from '@mui/material/Grid2';
import MedicalReportManager from './components/MedicalReportManager';
import FAQ from './components/FAQ';
import IubendaLinks from './components/IubendaLinks';
import PWAInstallBanner from './components/PWAInstallBanner';
import { useSubscription } from './hooks/useSubscription';
import { API_URL } from './config';
import './App.css';
import './styles/enterprise.css';

const logo = process.env.PUBLIC_URL + '/logo.webp';
const TermsOfUse = lazy(() => import('./components/TermsOfUse'));
const Help = lazy(() => import('./components/Help'));
const PrivacyPolicyPage = lazy(() => import('./components/PrivacyPolicyPage'));

// ---- Subscription badge (shown in header) ----
const SubscriptionBadge = ({ tier, onClick }) => (
  <button
    className={`sub-badge sub-badge--${tier.key}`}
    onClick={onClick}
    title={`Piano ${tier.label} — clicca per gestire l'abbonamento`}
    style={{
      fontFamily: 'inherit',
      background: 'rgba(255,255,255,0.15)',
      border: '1px solid rgba(255,255,255,0.30)',
      color: '#ffffff',
      backdropFilter: 'blur(8px)',
      WebkitBackdropFilter: 'blur(8px)',
    }}
  >
    <span className="sub-badge__dot" aria-hidden="true" style={{ background: '#ffffff', opacity: 0.85 }} />
    {tier.label}
  </button>
);

function App({ prefersDark, toggleDark }) {
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));
  const { tier, restoreToken } = useSubscription();
  const [checkoutActivating, setCheckoutActivating] = useState(false);

  // Poll backend after Stripe checkout redirect (?checkout_session_id=...)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('checkout_session_id');
    if (!sessionId) return;

    window.history.replaceState({}, '', window.location.pathname);
    setCheckoutActivating(true);

    let attempts = 0;
    const poll = setInterval(async () => {
      attempts++;
      try {
        const res = await fetch(`${API_URL}/subscription/status?session_id=${encodeURIComponent(sessionId)}`);
        const data = await res.json();
        if (data.status === 'active' && data.token) {
          clearInterval(poll);
          setCheckoutActivating(false);
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({ event: 'purchase_pro', value: 4.99, currency: 'EUR' });
          if (typeof window.gtag === 'function') {
            window.gtag('event', 'conversion', { 'send_to': 'AW-18013630934/asDnCJiknI8cENbjyI1D', 'value': 4.99, 'currency': 'EUR' });
            window.gtag('event', 'conversion', { 'send_to': 'AW-18013630934/IJGWCOmAr48cENbjyI1D', 'value': 4.99, 'currency': 'EUR', 'transaction_id': sessionId });
          }
          restoreToken(data.token);
        }
      } catch { /* retry */ }
      if (attempts >= 15) {
        clearInterval(poll);
        setCheckoutActivating(false);
      }
    }, 2000);

    return () => clearInterval(poll);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Restore Pro from ?restore=<token> link (email restoration flow)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('restore');
    if (!token) return;

    // Clean URL immediately
    window.history.replaceState({}, '', window.location.pathname);

    // Write token to subscription state + notify SW
    restoreToken(token);

    // Explicitly validate with backend (don't wait for effect re-run)
    fetch(`${API_URL}/subscription/validate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
    })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data?.tier && data.tier !== 'free') {
          console.log('[TRM] Token restored, tier:', data.tier);
        }
      })
      .catch(() => { /* offline — token already saved locally */ });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Pricing modal state lives in MedicalReportManager,
  // but we expose a global opener via a custom event for the header badge.
  const openPricingFromHeader = () => {
    window.dispatchEvent(new CustomEvent('trm:open-pricing'));
  };

  return (
    <Router>
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        backgroundColor: 'var(--e-bg, #F0F9FF)',
      }}>

        {/* ---- Header ---- */}
        <AppBar
          position="static"
          elevation={0}
          sx={{
            mb: 1,
            background: isMobile
              ? 'linear-gradient(160deg, #0A5C62 0%, #009090 45%, #00C8BE 100%)'
              : 'linear-gradient(105deg, #0A5C62 0%, #007A80 28%, #009090 58%, #00C8BE 100%)',
            borderBottom: 'none',
            boxShadow: '0 4px 24px rgba(10,92,98,0.30), 0 1px 0 rgba(0,200,190,0.28)',
            overflow: 'hidden',
            '&::after': {
              content: '""',
              position: 'absolute',
              inset: 0,
              background: 'radial-gradient(ellipse 60% 120% at 50% -20%, rgba(255,255,255,0.08) 0%, transparent 70%)',
              pointerEvents: 'none',
            },
          }}
        >
          <Toolbar sx={{ p: 1 }}>
            <Container maxWidth="md" sx={{ p: 0 }}>
              <Typography variant="h1" className="visually-hidden" sx={{ fontSize: 0 }}>
                Traduttore Referti Medici - traduzione referti, traduttore referti medici, traduzione documenti medici, lettura referto medico online, spiegazione referto medico, ai per referti medici, leggere referto medico, app per leggere referti medici, referti medici online, traduzione referto medico
              </Typography>

              {/* Logo row with badge */}
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
                py: isMobile ? 2 : 3,
              }}>
                <Box
                  component={Link}
                  to="/"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textDecoration: 'none',
                    color: 'inherit',
                    '&:hover img': {
                      transform: 'translateY(-2px)',
                      filter: 'brightness(0) invert(1) drop-shadow(0 4px 16px rgba(6,182,212,0.45))',
                    },
                  }}
                >
                  <Box
                    component="img"
                    src={logo}
                    alt="Logo Traduttore Referti Medici"
                    sx={{
                      height: isMobile ? 100 : 120,
                      transition: 'transform 0.3s ease, filter 0.3s ease',
                      filter: 'brightness(0) drop-shadow(0 2px 6px rgba(0,0,0,0.15))',
                    }}
                  />
                </Box>

                {/* Dark mode toggle + Subscription badge — top-right of header */}
                <Box sx={{
                  position: 'absolute',
                  right: 0,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                }}>
                  <IconButton
                    onClick={toggleDark}
                    size="small"
                    title={prefersDark ? 'Passa a modalità chiara' : 'Passa a modalità scura'}
                    sx={{ color: 'rgba(255,255,255,0.85)', '&:hover': { color: '#ffffff', background: 'rgba(255,255,255,0.12)' } }}
                  >
                    {prefersDark ? <LightModeIcon fontSize="small" /> : <DarkModeIcon fontSize="small" />}
                  </IconButton>
                  <SubscriptionBadge tier={tier} onClick={openPricingFromHeader} />
                </Box>
              </Box>
            </Container>
          </Toolbar>
        </AppBar>

        {/* ---- Checkout activation banner ---- */}
        {checkoutActivating && (
          <Box sx={{
            background: 'linear-gradient(90deg, #7c3aed, #a855f7)',
            color: '#fff',
            textAlign: 'center',
            py: 1,
            fontSize: '0.9rem',
            fontWeight: 500,
            letterSpacing: 0.2,
          }}>
            ⏳ Attivazione Pro in corso… attendere qualche secondo
          </Box>
        )}

        {/* ---- PWA install banner ---- */}
        <Container maxWidth="md" sx={{ px: isMobile ? 3 : 2, pt: 1 }}>
          <PWAInstallBanner />
        </Container>

        {/* ---- Main content ---- */}
        <Container
          maxWidth="md"
          sx={{
            px: isMobile ? 1 : 0,
            flexGrow: 1,
          }}
        >
          <Box sx={{ mt: 0 }}>
            <Suspense fallback={null}>
            <Routes>
              <Route path="/" element={<><MedicalReportManager /><FAQ /></>} />
              <Route path="/terms" element={<TermsOfUse />} />
              <Route path="/help" element={<Help />} />
              <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
            </Routes>
            </Suspense>
          </Box>
        </Container>

        <Footer isMobile={isMobile} />
      </Box>
    </Router>
  );
}

// ---- Footer CTA email copy ----
const EMAIL = 'info@traduttorerefertimedici.it';
const EmailCopyButton = () => {
  const [copied, setCopied] = useState(false);
  const handleClick = (e) => {
    e.preventDefault();
    navigator.clipboard.writeText(EMAIL).then(() => {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: 'click_cta_email_copy' });
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  return (
    <a href={`mailto:${EMAIL}`} className="footer-cta__email" onClick={handleClick}>
      <span className="footer-cta__email-icon">{copied ? '✓' : '✉'}</span>
      {copied ? 'Copiato!' : EMAIL}
    </a>
  );
};

// ---- Footer ----
const Footer = ({ isMobile }) => (
  <Box component="footer" sx={{ mt: 'auto' }}>

    {/* CTA Partnership */}
    <section className="footer-cta">
      <div className="footer-cta__inner">
        <div className="footer-cta__tag">Partnership &amp; ADV</div>
        <h2 className="footer-cta__headline">
          Vuoi integrare TRM<br />nella tua app?
        </h2>
        <p className="footer-cta__sub">
          Spazi pubblicitari, integrazioni API, white-label.<br />
          Sentiamoci.
        </p>
        <EmailCopyButton />
      </div>
      <div className="footer-cta__deco" aria-hidden="true" />
    </section>

    {/* CTA Medici Specialisti */}
    <section className="medici-cta">
      <p className="medici-cta__text">
        Sei un medico specialista? Ricevi pazienti che hanno tradotto un referto nella tua zona.
      </p>
      <a
        href="mailto:info@traduttorerefertimedici.it?subject=Network TRM Medico"
        className="btn-medici-gratis"
        onClick={() => { window.dataLayer = window.dataLayer || []; window.dataLayer.push({ event: 'click_cta_specialists' }); }}
      >
        Contattaci ed entra nel network TRM gratuitamente!
      </a>
    </section>

    <Box
      sx={{
        borderTop: '1px solid var(--e-border, #E0EAF4)',
        backgroundColor: 'var(--e-surface, #FFFFFF)',
        py: isMobile ? 1.5 : 2,
      }}
    >
    <Grid
      container
      spacing={isMobile ? 1 : 2}
      direction={isMobile ? 'column-reverse' : 'row'}
      justifyContent={isMobile ? 'center' : 'space-between'}
      alignItems="center"
    >
      <Grid item xs={12} sm={4}>
        <Typography
          variant="body2"
          align="center"
          sx={{
            fontWeight: 600,
            fontSize: isMobile ? '0.72rem' : '0.82rem',
            color: 'text.secondary',
            ml: isMobile ? 0 : 2,
            mt: isMobile ? 1 : 0,
          }}
        >
          Traduttore Referti Medici · 2026
        </Typography>
      </Grid>

      <Grid item xs={12} sm={8}>
        <Box sx={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'center',
          gap: isMobile ? 1 : 2,
          textAlign: 'center',
        }}>
          <FooterLink to="/help" label="Aiuto" isMobile={isMobile} />
          <FooterLink to="/terms" label="Termini d'Uso" isMobile={isMobile} />
          <FooterButton label="Segnala Problema" isMobile={isMobile} onClick={() => window.dispatchEvent(new Event('open-report-modal'))} />
          <IubendaLinksResponsive isMobile={isMobile} />
        </Box>
      </Grid>
    </Grid>
    </Box>
  </Box>
);

const FooterButton = ({ label, isMobile, onClick }) => (
  <Typography
    variant="body2"
    sx={{
      whiteSpace: 'nowrap',
      fontSize: isMobile ? '0.72rem' : '0.82rem',
      py: isMobile ? 0.5 : 0,
      color: 'text.secondary',
      cursor: 'pointer',
      '&:hover': { textDecoration: 'underline' },
    }}
    onClick={onClick}
  >
    {label}
  </Typography>
);

const FooterLink = ({ to, label, isMobile }) => (
  <Typography
    variant="body2"
    sx={{
      whiteSpace: 'nowrap',
      fontSize: isMobile ? '0.72rem' : '0.82rem',
      py: isMobile ? 0.5 : 0,
      color: 'text.secondary',
    }}
  >
    <Link to={to} style={{ textDecoration: 'none', color: 'inherit' }}>
      {label}
    </Link>
  </Typography>
);

const IubendaLinksResponsive = ({ isMobile }) => (
  <Box sx={{
    '& .iubenda-white': {
      fontSize: isMobile ? '0.72rem' : '0.82rem',
      py: isMobile ? 0.5 : 0,
      display: 'inline-block',
      color: 'text.secondary',
    },
  }}>
    <IubendaLinks />
  </Box>
);

export default App;
